import React from 'react';
import {Routes, Route, Navigate} from 'react-router-dom';
import DefaultLayout from '../layout/DefaultLayout';

import ModelList from '../pages/model/list/ModelList';
import ModelDetail from '../pages/model/detail/ModelDetail';

import UserDetail from '../pages/admin/user/detail/UserDetail';
import UserList from '../pages/admin/user/list/UserList';
import ExternalUserList from '../pages/admin/externalization/list/UserList';
import UserEdit from '../pages/admin/user/edit/UserEdit';
import {MRAppRoutes} from './routes';
import UserEnroll from '../pages/admin/user/enroll/UserEnroll';
import ExternalUserEnroll from '../pages/admin/externalization/enroll/ExternalUserEnroll';
import {Error404Page} from '../pages/error/Error404Page';
import ScenarioExecutionQueue from '../pages/scenarioExecutionQueue/ScenarioExecutionQueue';
import IncidentList from '../pages/admin/incidentManagement/IncidentList';
import ManageMetadataList from '../pages/admin/manageMetadata/list/List';
import ModelUploadWrapper from '../pages/modelUpload/ModelUploadWrapper';
import SubModelList from '../pages/subModel/list/SubModelList';
import ModelInputEditor from '../pages/modelInput/editor/ModelInputEditor';
import ModelInputList from '../pages/modelInput/list/ModelInputList';
import ScenarioList from '../pages/scenario/list/ScenarioList';
import AllNotifications from '../pages/Notifications/AllNotifications';
import ScenarioParametersEditor from '../pages/scenario/editor/ScenarioParametersEditor';
import ScenarioDetailLazy from '../pages/scenario/detail/ScenarioDetailLazy';
import ScenarioParametersView from '../pages/scenario/view/ScenarioParametersView';
import VisualizationStep1 from '../pages/visualization/step1/VisualizationStep1';
import VisualizationStep2 from '../pages/visualization/step2/VisualizationStep2';
import VisualizationStep3 from '../pages/visualization/step3/VisualizationStep3';
import VisualizationParametersDisplay from '../pages/visualization/parametersDisplay/VisualizationParametersDisplay';
import {HomePage} from '../auth/HomePage';
import LoginHomePage from '../auth/LoginHomePage';
import RegistrationReview from '../pages/admin/user/registration/RegistrationReview';
import {Filter} from '../pages/filter/Filter';
import ProtectedRoute from './ProtectedRoute';
import RootRedirect from './RootRedirect';

const Router: React.FC = () => {
  return (
    <Routes>
      {/* uploader pages */}
      <Route
        path={MRAppRoutes.MODEL_UPLOAD}
        element={<DefaultLayout component={ModelUploadWrapper} theme="whiteTheme" />}
      />

      {/* scenario pages */}
      <Route
        path={MRAppRoutes.SCENARIO_PARAMETERS_EDITOR}
        element={<DefaultLayout component={ScenarioParametersEditor} theme="greyTheme" />}
      />
      <Route
        path={MRAppRoutes.SCENARIO_PARAMETERS_VIEW}
        element={<DefaultLayout component={ScenarioParametersView} theme="greyTheme" />}
      />
      <Route path={MRAppRoutes.SCENARIO_LIST} element={<DefaultLayout component={ScenarioList} theme="greyTheme" />} />
      <Route
        path={MRAppRoutes.SCENARIO_DETAIL}
        element={<DefaultLayout component={ScenarioDetailLazy} theme="greyTheme" />}
      />

      {/* modelInput pages */}
      <Route
        path={MRAppRoutes.MODEL_INPUT_EDITOR}
        element={<DefaultLayout component={ModelInputEditor} theme="greyTheme" />}
      />
      <Route
        path={MRAppRoutes.MODEL_INPUT_LIST}
        element={<DefaultLayout component={ModelInputList} theme="greyTheme" />}
      />

      {/* sub-model pages */}
      <Route path={MRAppRoutes.SUBMODEL_LIST} element={<DefaultLayout component={SubModelList} theme="greyTheme" />} />

      {/* visualization pages */}
      <Route
        path={MRAppRoutes.VISUALIZATION_STEP_1}
        element={<DefaultLayout component={VisualizationStep1} theme="whiteTheme" />}
      />
      <Route
        path={MRAppRoutes.VISUALIZATION_STEP_2}
        element={<DefaultLayout component={VisualizationStep2} theme="whiteTheme" />}
      />
      <Route
        path={MRAppRoutes.VISUALIZATION_STEP_3}
        element={<DefaultLayout component={VisualizationStep3} theme="whiteTheme" />}
      />
      <Route
        path={MRAppRoutes.VISUALIZATION_PARAMETERS_DISPLAY}
        element={<DefaultLayout component={VisualizationParametersDisplay} theme="whiteTheme" />}
      />

      {/* model scenario pages */}
      <Route
        path={MRAppRoutes.SCENARIO_EXECUTION_QUEUE}
        element={<DefaultLayout component={ScenarioExecutionQueue} theme="darkGreyTheme" />}
      />

      {/* externalization pages */}
      <Route
        path={MRAppRoutes.EXTERNALIZATION_LIST}
        element={<DefaultLayout component={ExternalUserList} theme="greyTheme" />}
      />
      <Route
        path={MRAppRoutes.EXTERNALIZATION_ENROLL}
        element={<DefaultLayout component={ExternalUserEnroll} theme="greyTheme" />}
      />
      <Route path={MRAppRoutes.USER_REGISTRATION} element={<HomePage />} />
      <Route path={MRAppRoutes.LANDING_PAGE} element={<LoginHomePage />} />

      {/* model pages - Model List accessible by BOTH user types */}
      <Route path={MRAppRoutes.MODEL_DETAIL} element={<DefaultLayout component={ModelDetail} theme="greyTheme" />} />
      <Route path={MRAppRoutes.MODEL_LIST} element={<DefaultLayout component={ModelList} theme="whiteTheme" />} />

      {/* filter page - PROTECTED: Internal users only */}
      <Route
        path={MRAppRoutes.FILTER_PAGE}
        element={
          <ProtectedRoute requireInternalUser redirectTo={MRAppRoutes.MODEL_LIST}>
            <DefaultLayout component={Filter} theme="whiteTheme" />
          </ProtectedRoute>
        }
      />

      {/* admin section */}
      <Route path={MRAppRoutes.USER_DETAIL} element={<DefaultLayout component={UserDetail} />} />
      <Route path={MRAppRoutes.USER_EDIT} element={<DefaultLayout component={UserEdit} />} />
      <Route path={MRAppRoutes.USER_ENROLL} element={<DefaultLayout component={UserEnroll} />} />
      <Route path={MRAppRoutes.USER_LIST} element={<DefaultLayout component={UserList} />} />

      <Route path={MRAppRoutes.MANAGE_METADATA_PROPERTY} element={<DefaultLayout component={ManageMetadataList} />} />
      <Route path={MRAppRoutes.MANAGE_METADATA} element={<DefaultLayout component={ManageMetadataList} />} />

      <Route path={MRAppRoutes.INCIDENT_MANAGEMENT} element={<DefaultLayout component={IncidentList} />} />

      <Route path={MRAppRoutes.ADMIN_EXTERNALIZATION_LIST} element={<DefaultLayout component={ExternalUserList} />} />

      <Route
        path={MRAppRoutes.REGISTRATION_REVIEW}
        element={<DefaultLayout component={RegistrationReview} theme="greyTheme" />}
      />
      {/* All notifications page */}
      <Route
        path={MRAppRoutes.ALL_NOTIFICATIONS}
        element={<DefaultLayout component={AllNotifications} theme="darkTheme" />}
      />

      {/* Redirects */}
      <Route path={MRAppRoutes.ADMIN} element={<Navigate to={MRAppRoutes.USER_LIST} replace />} />
      <Route path={MRAppRoutes.EXTERNALIZATION} element={<Navigate to={MRAppRoutes.EXTERNALIZATION_LIST} replace />} />

      {/* ROOT REDIRECT: Internal → Filter, External → Model List */}
      <Route path={MRAppRoutes.ROOT} element={<RootRedirect />} />

      {/* 404 fallback */}
      <Route path="*" element={<Error404Page />} />
    </Routes>
  );
};

export default Router;
